import type { StringTable } from '#contract'
import { enOs } from './en-os.ts'

/** The UI's own strings. Keys are dot paths grouped by screen; content brings its own table. */
export const en = {
  'app.title': 'Nordic Model',

  'boot.loading': 'Loading',

  'loading.label': 'Loading',
  'loading.level': 'Loading the level',
  'loading.assets': 'Loading textures, models and sounds',
  'loading.building': 'Building the level',
  'loading.shaders': 'Preparing shaders',
  'loading.items': 'Preparing items',

  'menu.continue': 'Continue',
  'menu.newGame': 'New game',
  'menu.loadGame': 'Load game',
  'menu.settings': 'Settings',
  'menu.credits': 'Credits',

  'saves.newGame': 'New game',
  'saves.loadGame': 'Load game',
  'saves.back': 'Back',
  'saves.slot': 'Slot {n}',
  'saves.empty': 'Empty',
  'saves.damaged': 'Damaged',
  'saves.damagedHint': 'This save can’t be read.',
  'saves.when': '{day} · {time}',
  'saves.whenMoney': '{when} · {money}',
  'saves.details': '{played} played · {savedAt}',
  'saves.deleteSlot': 'Delete slot {n}',
  'saves.deleteTitle': 'Delete slot {n}?',
  'saves.deleteAction': 'Delete',
  'saves.overwriteTitle': 'Overwrite slot {n}?',
  'saves.overwriteAction': 'Overwrite',
  'saves.lost': '{when} will be lost.',
  'saves.lostThis': 'This save will be lost.',
  'saves.cancel': 'Cancel',

  'calendar.day': '{weekday} · {week}',
  'calendar.week': 'Week {n}',
  'time.hoursMinutes': '{h} h {m} min',
  'time.minutes': '{m} min',

  'tasks.more': '+{n} more',
  'hud.notification.now': 'now',
  'tasks.eta.due': 'now',
  'tasks.eta.soon': '< 15 min',
  'tasks.eta.minutes': '{m} min',
  'tasks.eta.hours': '{h} h',
  'tasks.eta.hoursMinutes': '{h} h {m} min',
  'wallet.label': 'Balance: {money}',

  'toast.saved': 'Game saved',
  'toast.saveFailed': 'Couldn’t save the game',

  'pause.title': 'Paused',
  'pause.resume': 'Resume',
  'pause.quit': 'Quit to main menu',
  'pause.settings': 'Settings',
  'pause.credits': 'Credits',
  'pause.unsaved': 'Progress since you last slept will be lost.',

  'hud.captureHint': 'Click to look around',
  'hud.fps': '{fps} FPS',

  'settings.title': 'Settings',
  'settings.back': 'Back',
  'settings.reset': 'Reset to defaults',
  'settings.tabs.graphics': 'Graphics',
  'settings.tabs.audio': 'Audio',
  'settings.tabs.controls': 'Controls',
  'settings.tabs.accessibility': 'Accessibility',
  'settings.tabs.developer': 'Developer',
  'settings.preset.low': 'Low',
  'settings.preset.medium': 'Medium',
  'settings.preset.high': 'High',
  'settings.preset.ultra': 'Ultra',
  'settings.preset.custom': 'Custom',
  'settings.level.off': 'Off',
  'settings.level.low': 'Low',
  'settings.level.high': 'High',
  'settings.graphics.preset': 'Preset',
  'settings.graphics.preset.description': 'Changing an option selects Custom.',
  'settings.graphics.renderer': 'Renderer',
  'settings.graphics.renderer.description': 'Applied the next time the game starts.',
  'settings.graphics.renderer.webgl2': 'WebGL2',
  'settings.graphics.renderer.webgpu': 'WebGPU',
  'settings.graphics.restart': 'Restart the game to change the renderer.',
  'settings.graphics.restartNow': 'Restart now',
  'settings.graphics.resolution': 'Resolution',
  'settings.graphics.antialiasing': 'Anti-aliasing',
  'settings.graphics.antialiasing.fxaa': 'FXAA',
  'settings.graphics.antialiasing.msaa': 'MSAA',
  'settings.graphics.shadows': 'Shadows',
  'settings.graphics.ambientOcclusion': 'Ambient occlusion',
  'settings.graphics.reflections': 'Reflections',
  'settings.graphics.bloom': 'Bloom',
  'settings.graphics.fov': 'Field of view',
  'settings.graphics.frameRate': 'Frame rate',
  'settings.graphics.frameRate.description':
    'Renders every frame; the display’s refresh rate is the ceiling.',
  'settings.graphics.frameRate.unlimited': 'Uncapped',
  'settings.graphics.frameRate.30': '30',
  'settings.graphics.frameRate.60': '60',
  'settings.graphics.showFps': 'Show FPS',
  'settings.notice.webgpuFallback': 'WebGPU isn’t available, so the game is using WebGL2.',
  'settings.audio.master': 'Master',
  'settings.audio.music': 'Music',
  'settings.audio.sfx': 'Effects',
  'settings.audio.voice': 'Voice',
  'settings.controls.mouse': 'Mouse sensitivity',
  'settings.controls.gamepad': 'Gamepad sensitivity',
  'settings.controls.touch': 'Touch sensitivity',
  'settings.controls.invertY': 'Invert Y',
  'settings.controls.bindings': 'Keyboard and mouse',
  'settings.controls.pressKey': 'Press a key…',
  'settings.controls.reserved': 'That key can’t be bound.',
  'settings.controls.moved': 'Moved from {action}',
  'settings.controls.resetBindings': 'Reset bindings',
  'settings.controls.unbound': 'Unbound',
  'settings.controls.gamepadLayout': 'Gamepad',
  'settings.controls.stick.move': 'Left stick',
  'settings.controls.stick.look': 'Right stick',
  'settings.binding.moveForward': 'Move forward',
  'settings.binding.moveBack': 'Move back',
  'settings.binding.moveLeft': 'Move left',
  'settings.binding.moveRight': 'Move right',
  'settings.binding.jump': 'Jump',
  'settings.binding.sprint': 'Sprint',
  'settings.binding.interact': 'Interact',
  'settings.binding.skip': 'Skip',
  'settings.binding.useItem': 'Use item (hold)',
  'settings.binding.drop': 'Drop item (hold)',
  'settings.binding.inventory': 'Inventory',
  'settings.binding.phone': 'Phone',
  'settings.binding.look': 'Look',
  'settings.binding.pause': 'Pause',
  'settings.accessibility.subtitles': 'Subtitles',
  'settings.accessibility.reducedMotion': 'Reduced motion',
  'settings.accessibility.reducedMotion.description': 'Turns off head bob and interface motion.',
  'settings.developer.enabled': 'Developer mode',
  'settings.developer.description': 'Enables developer tools for this game.',

  'dev.console.open': 'Open the console',
  'dev.console.input': 'Console command',
  'dev.stats.fps': '{fps} fps',
  'dev.stats.frame': '{ms} ms',
  'dev.stats.gpu': 'GPU {ms}',
  'dev.stats.gpuNa': 'GPU n/a',
  'dev.stats.draws': '{count} draws',
  'dev.stats.meshes': '{count} meshes',
  'dev.stats.textures': '{count} textures',

  'hud.hold': 'Hold',

  'inventory.open': 'Inventory',
  'inventory.close': 'Close',
  'inventory.bin': 'Bin',
  'inventory.empty': 'Empty',
  'inventory.quickMove': 'Quick move',
  'inventory.count': '{count} / {max}',
  'inventory.consumable': 'Consumable',
  'inventory.tag.food': 'Food',
  'inventory.tag.drink': 'Drink',
  'inventory.hint.takeAll': 'Take all',
  'inventory.hint.takeOne': 'Take one',
  'inventory.hint.putAll': 'Put all',
  'inventory.hint.putOne': 'Put one',
  'inventory.hint.move': 'Move across',
  'inventory.hint.close': 'Close',
  'inventory.hint.tap': 'Tap',

  'touch.jump': 'Jump',
  'touch.pause': 'Pause',
  'touch.phone': 'Phone',

  ...enOs,

  'rotate.title': 'Rotate your device',
  'rotate.body': 'The game plays in landscape.',

  'credits.title': 'Credits',
  'credits.back': 'Back',
  'credits.loading': 'Loading the credits…',
  'credits.error': 'The credits couldn’t be loaded.',
  'credits.by': 'by {authors}',
  'credits.group.artworks': 'Artworks',
  'credits.group.materials': 'Materials',
  'credits.group.models': 'Models',
  'credits.group.environment': 'Environment',
  'credits.group.music': 'Music',
  'credits.group.sound-effects': 'Sound effects',
  'credits.group.voice': 'Voice',
  'credits.group.software': 'Fonts and software',

  'error.bootFailed': 'The game failed to start',
  'error.loadFailed': 'The game failed to load',
  'error.levelNotFound': 'This level doesn’t exist',
  'error.levelLoadFailed': 'The level failed to load',
  'error.unsupportedDevice': 'This device can’t run the game',
  'error.hint':
    'Reload the page to try again. If it keeps happening, include these details in a bug report.',
  'error.reload': 'Reload',
} as const satisfies StringTable
